import { useCallback, useEffect, useState } from 'react';
import { logEvent, analytics } from '../firebase.js';
import { AddToCollectionModal } from './AddToCollectionModal.jsx';
import { CollectionService } from '../services/collection-service.js';
import { useLocale } from '../hooks/useLocale.jsx';

// Unified "save to collection" button (Phase 10-4 option B).
// Replaces the old BookmarkButton — bookmarks and collections were the same
// concept under two names. Click opens the collection picker; user picks a
// board (or creates one). The default 「Saved」 collection auto-creates inside
// the modal if the user has no collections yet.
export function SaveButton({ designId, user, onSignInRequest, variant = 'icon' }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  // 이미 어떤 컬렉션에든 담겨 있는지. 담겨 있으면 아이콘을 채운 형태로
  // 바꿔 "저장됨" 을 한눈에 보이게 한다 (예전엔 담아도 테두리 아이콘 그대로라
  // 눌렀는지 안 눌렀는지 알 수 없었다).
  const [saved, setSaved] = useState(false);

  const isLoggedIn = user && !user.isAnonymous;

  const refreshSaved = useCallback(async () => {
    if (!isLoggedIn || !designId) { setSaved(false); return; }
    try {
      const cols = await CollectionService.listMine(user.uid);
      const members = await CollectionService.checkMembership(cols, designId);
      setSaved(members.size > 0);
    } catch { /* 표시용이라 실패해도 조용히 넘어간다 */ }
  }, [isLoggedIn, user?.uid, designId]);

  useEffect(() => { refreshSaved(); }, [refreshSaved]);

  // 모달을 닫을 때 다시 확인 — 그 안에서 담거나 뺐을 수 있다.
  const closeModal = () => { setOpen(false); refreshSaved(); };

  const handleClick = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!isLoggedIn) { onSignInRequest?.(); return; }
    logEvent(analytics, 'save_button_clicked');
    setOpen(true);
  };

  if (variant === 'pill') {
    return (
      <>
        <button
          type="button"
          className={`btn btn-secondary bookmark-pill${saved ? ' is-saved' : ''}`}
          onClick={handleClick}
          title={t('saveToCollection')}
        >
          <i className="material-symbols-rounded">bookmark</i>
          {t('saved')}
        </button>
        {open && (
          <AddToCollectionModal designId={designId} user={user} onClose={closeModal} />
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`bookmark-btn${saved ? ' is-saved' : ''}`}
        onClick={handleClick}
        title={t('saveToCollection')}
        aria-label={t('saveToCollection')}
      >
        <i className="material-symbols-rounded">bookmark</i>
      </button>
      {open && (
        <AddToCollectionModal designId={designId} user={user} onClose={closeModal} />
      )}
    </>
  );
}
